import React, { useEffect, useRef, useState } from 'react';
import { collection, query, where, getDocs, getDoc, doc, updateDoc, addDoc, deleteDoc, setDoc } from 'firebase/firestore';
import { db } from '../config';
import { auth } from '../config';
import { onAuthStateChanged } from 'firebase/auth';
import excelNotificationService from '../services/excelNotificationService';
import { excelAwsNotificationService } from '../services/excelAwsNotificationService';
import excelAwsS3Service, { ExcelWorkbook } from '../services/excelAwsS3Service';
import UserSelector from './UserSelector';
import './ExcelContainer.css';

// Import LuckySheet
declare global {
  interface Window {
    luckysheet: any;
  }
}

interface LuckySheetData {
  name: string;
  data: any[][];
  config?: any;
}

interface ExcelContainerProps {
  folderId: string;
  folderName: string;
  onClose: () => void;
}

interface ExcelData {
  tasks: any[];
  plans: any[];
  blueprints: any[];
  targets: any[];
}

interface User {
  id: string;
  name: string;
  email: string;
  color: string;
}

const ExcelContainer: React.FC<ExcelContainerProps> = ({ folderId, folderName, onClose }) => {
  const excelRef = useRef<HTMLDivElement>(null);
  const [excelData, setExcelData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [isExcelReady, setIsExcelReady] = useState(false);
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string>('');

  useEffect(() => {
    // Load LuckySheet script
    loadLuckySheetScript();
    
    // Get current user
    onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser({
          id: user.uid,
          name: user.displayName || user.email || 'Unknown User',
          email: user.email || '',
          color: getRandomColor()
        });
      }
    });

    // Load folder data
    loadFolderData();
  }, [folderId]);

  const loadLuckySheetScript = () => {
    if (window.luckysheet) {
      setIsExcelReady(true);
      return;
    }

    // Load CSS first
    const loadCSS = (href: string) => {
      return new Promise((resolve) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.onload = () => resolve(true);
        document.head.appendChild(link);
      });
    };

    // Load JavaScript
    const loadJS = (src: string) => {
      return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = src;
        script.onload = () => resolve(true);
        script.onerror = () => {
          console.error('Failed to load LuckySheet script');
          resolve(false);
        };
        document.head.appendChild(script);
      });
    };

    // Load all resources
    Promise.all([
      loadCSS('https://cdn.jsdelivr.net/npm/luckysheet@latest/dist/css/luckysheet.css'),
      loadCSS('https://cdn.jsdelivr.net/npm/luckysheet@latest/dist/plugins/css/pluginsCss.css'),
      loadCSS('https://cdn.jsdelivr.net/npm/luckysheet@latest/dist/plugins/plugins.css'),
      loadJS('https://cdn.jsdelivr.net/npm/luckysheet@latest/dist/luckysheet.umd.js')
    ]).then((results) => {
      if (results.every(result => result)) {
        console.log('✅ LuckySheet loaded successfully');
        setIsExcelReady(true);
      } else {
        console.error('❌ Failed to load LuckySheet resources');
        setIsLoading(false);
      }
    });
  };

  const loadFolderData = async () => {
    try {
      setIsLoading(true);
      
      // Load all data from folder
      const [tasks, plans, blueprints, targets, allUsers] = await Promise.all([
        loadTasks(),
        loadPlans(),
        loadBlueprints(),
        loadTargets(),
        loadUsers()
      ]);

      setUsers(allUsers);
      
      // Convert to Excel format
      const excelData = convertToExcelFormat(tasks, plans, blueprints, targets);
      setExcelData(excelData);
      
    } catch (error) {
      console.error('Error loading folder data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTasks = async () => {
    const tasksRef = collection(db, 'tasks');
    const q = query(tasksRef, where('folderId', '==', folderId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  const loadPlans = async () => {
    const plansRef = collection(db, 'plans');
    const q = query(plansRef, where('folderId', '==', folderId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  const loadBlueprints = async () => {
    const blueprintsRef = collection(db, 'blueprints');
    const q = query(blueprintsRef, where('folderId', '==', folderId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  const loadTargets = async () => {
    const targetsRef = collection(db, 'targets');
    const q = query(targetsRef, where('folderId', '==', folderId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  const loadUsers = async () => {
    const usersRef = collection(db, 'users');
    const querySnapshot = await getDocs(usersRef);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      name: doc.data().displayName || doc.data().email || 'Unknown User',
      email: doc.data().email || '',
      color: getRandomColor()
    }));
  };

  const convertToExcelFormat = (tasks: any[], plans: any[], blueprints: any[], targets: any[]) => {
    return [
      {
        name: 'Tasks',
        color: '#ff6b6b',
        index: 0,
        status: 1,
        order: 0,
        data: [
          [
            { v: 'Task ID', ct: { fa: 'General', t: 'g' }, m: 'Task ID', bg: '#f0f0f0' },
            { v: 'Title', ct: { fa: 'General', t: 'g' }, m: 'Title', bg: '#f0f0f0' },
            { v: 'Status', ct: { fa: 'General', t: 'g' }, m: 'Status', bg: '#f0f0f0' },
            { v: 'Priority', ct: { fa: 'General', t: 'g' }, m: 'Priority', bg: '#f0f0f0' },
            { v: 'Assigned To', ct: { fa: 'General', t: 'g' }, m: 'Assigned To', bg: '#f0f0f0' },
            { v: 'Due Date', ct: { fa: 'General', t: 'g' }, m: 'Due Date', bg: '#f0f0f0' },
            { v: 'Created By', ct: { fa: 'General', t: 'g' }, m: 'Created By', bg: '#f0f0f0' },
            { v: 'Last Updated', ct: { fa: 'General', t: 'g' }, m: 'Last Updated', bg: '#f0f0f0' }
          ],
          ...tasks.map(task => [
            { v: task.id, ct: { fa: 'General', t: 'g' }, m: task.id },
            { v: task.title || '', ct: { fa: 'General', t: 'g' }, m: task.title || '' },
            { v: task.status || '', ct: { fa: 'General', t: 'g' }, m: task.status || '' },
            { v: task.priority || '', ct: { fa: 'General', t: 'g' }, m: task.priority || '' },
            { v: task.assignedTo || '', ct: { fa: 'General', t: 'g' }, m: task.assignedTo || '' },
            { v: task.dueDate ? new Date(task.dueDate).toLocaleDateString() : '', ct: { fa: 'General', t: 'g' }, m: task.dueDate ? new Date(task.dueDate).toLocaleDateString() : '' },
            { v: task.createdBy || '', ct: { fa: 'General', t: 'g' }, m: task.createdBy || '' },
            { v: task.updatedAt ? new Date(task.updatedAt).toLocaleDateString() : '', ct: { fa: 'General', t: 'g' }, m: task.updatedAt ? new Date(task.updatedAt).toLocaleDateString() : '' }
          ])
        ],
        config: {
          merge: {},
          rowlen: { 0: 20 },
          columnlen: { 0: 100, 1: 200, 2: 100, 3: 100, 4: 150, 5: 120, 6: 150, 7: 120 },
          rowhidden: {},
          colhidden: {},
          customHeight: {},
          customWidth: {}
        }
      },
      {
        name: 'Plans',
        color: '#4ecdc4',
        index: 1,
        status: 1,
        order: 1,
        data: [
          [
            { v: 'Plan ID', ct: { fa: 'General', t: 'g' }, m: 'Plan ID', bg: '#f0f0f0' },
            { v: 'Title', ct: { fa: 'General', t: 'g' }, m: 'Title', bg: '#f0f0f0' },
            { v: 'Status', ct: { fa: 'General', t: 'g' }, m: 'Status', bg: '#f0f0f0' },
            { v: 'Start Date', ct: { fa: 'General', t: 'g' }, m: 'Start Date', bg: '#f0f0f0' },
            { v: 'End Date', ct: { fa: 'General', t: 'g' }, m: 'End Date', bg: '#f0f0f0' },
            { v: 'Progress', ct: { fa: 'General', t: 'g' }, m: 'Progress', bg: '#f0f0f0' },
            { v: 'Owner', ct: { fa: 'General', t: 'g' }, m: 'Owner', bg: '#f0f0f0' }
          ],
          ...plans.map(plan => [
            { v: plan.id, ct: { fa: 'General', t: 'g' }, m: plan.id },
            { v: plan.title || '', ct: { fa: 'General', t: 'g' }, m: plan.title || '' },
            { v: plan.status || '', ct: { fa: 'General', t: 'g' }, m: plan.status || '' },
            { v: plan.startDate ? new Date(plan.startDate).toLocaleDateString() : '', ct: { fa: 'General', t: 'g' }, m: plan.startDate ? new Date(plan.startDate).toLocaleDateString() : '' },
            { v: plan.endDate ? new Date(plan.endDate).toLocaleDateString() : '', ct: { fa: 'General', t: 'g' }, m: plan.endDate ? new Date(plan.endDate).toLocaleDateString() : '' },
            { v: plan.progress || '', ct: { fa: 'General', t: 'g' }, m: plan.progress || '' },
            { v: plan.owner || '', ct: { fa: 'General', t: 'g' }, m: plan.owner || '' }
          ])
        ],
        config: {
          merge: {},
          rowlen: { 0: 20 },
          columnlen: { 0: 100, 1: 200, 2: 100, 3: 120, 4: 120, 5: 100, 6: 150 },
          rowhidden: {},
          colhidden: {},
          customHeight: {},
          customWidth: {}
        }
      },
      {
        name: 'Blueprints',
        color: '#45b7d1',
        index: 2,
        status: 1,
        order: 2,
        data: [
          [
            { v: 'Blueprint ID', ct: { fa: 'General', t: 'g' }, m: 'Blueprint ID', bg: '#f0f0f0' },
            { v: 'Title', ct: { fa: 'General', t: 'g' }, m: 'Title', bg: '#f0f0f0' },
            { v: 'Version', ct: { fa: 'General', t: 'g' }, m: 'Version', bg: '#f0f0f0' },
            { v: 'Status', ct: { fa: 'General', t: 'g' }, m: 'Status', bg: '#f0f0f0' },
            { v: 'Created By', ct: { fa: 'General', t: 'g' }, m: 'Created By', bg: '#f0f0f0' },
            { v: 'Category', ct: { fa: 'General', t: 'g' }, m: 'Category', bg: '#f0f0f0' }
          ],
          ...blueprints.map(blueprint => [
            { v: blueprint.id, ct: { fa: 'General', t: 'g' }, m: blueprint.id },
            { v: blueprint.title || '', ct: { fa: 'General', t: 'g' }, m: blueprint.title || '' },
            { v: blueprint.version || '', ct: { fa: 'General', t: 'g' }, m: blueprint.version || '' },
            { v: blueprint.status || '', ct: { fa: 'General', t: 'g' }, m: blueprint.status || '' },
            { v: blueprint.createdBy || '', ct: { fa: 'General', t: 'g' }, m: blueprint.createdBy || '' },
            { v: blueprint.category || '', ct: { fa: 'General', t: 'g' }, m: blueprint.category || '' }
          ])
        ],
        config: {
          merge: {},
          rowlen: { 0: 20 },
          columnlen: { 0: 100, 1: 200, 2: 100, 3: 100, 4: 150, 5: 150 },
          rowhidden: {},
          colhidden: {},
          customHeight: {},
          customWidth: {}
        }
      },
      {
        name: 'Targets',
        color: '#96ceb4',
        index: 3,
        status: 1,
        order: 3,
        data: [
          [
            { v: 'Target ID', ct: { fa: 'General', t: 'g' }, m: 'Target ID', bg: '#f0f0f0' },
            { v: 'Title', ct: { fa: 'General', t: 'g' }, m: 'Title', bg: '#f0f0f0' },
            { v: 'Status', ct: { fa: 'General', t: 'g' }, m: 'Status', bg: '#f0f0f0' },
            { v: 'Target Date', ct: { fa: 'General', t: 'g' }, m: 'Target Date', bg: '#f0f0f0' },
            { v: 'Progress', ct: { fa: 'General', t: 'g' }, m: 'Progress', bg: '#f0f0f0' },
            { v: 'Assigned To', ct: { fa: 'General', t: 'g' }, m: 'Assigned To', bg: '#f0f0f0' }
          ],
          ...targets.map(target => [
            { v: target.id, ct: { fa: 'General', t: 'g' }, m: target.id },
            { v: target.title || '', ct: { fa: 'General', t: 'g' }, m: target.title || '' },
            { v: target.status || '', ct: { fa: 'General', t: 'g' }, m: target.status || '' },
            { v: target.targetDate ? new Date(target.targetDate).toLocaleDateString() : '', ct: { fa: 'General', t: 'g' }, m: target.targetDate ? new Date(target.targetDate).toLocaleDateString() : '' },
            { v: target.progress || '', ct: { fa: 'General', t: 'g' }, m: target.progress || '' },
            { v: target.assignedTo || '', ct: { fa: 'General', t: 'g' }, m: target.assignedTo || '' }
          ])
        ],
        config: {
          merge: {},
          rowlen: { 0: 20 },
          columnlen: { 0: 100, 1: 200, 2: 100, 3: 120, 4: 100, 5: 150 },
          rowhidden: {},
          colhidden: {},
          customHeight: {},
          customWidth: {}
        }
      }
    ];
  };

  const getRandomColor = () => {
    const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57', '#ff9ff3', '#54a0ff'];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  useEffect(() => {
    if (isExcelReady && excelData && excelRef.current) {
      initializeExcel();
    }
  }, [isExcelReady, excelData]);

  const initializeExcel = () => {
    if (!excelRef.current) return;

    // Try LuckySheet first
    if (window.luckysheet) {
      try {
        window.luckysheet.create({
          container: excelRef.current,
          data: excelData,
          title: folderName,
          userInfo: currentUser ? [currentUser.id] : [],
          myFolderUrl: '',
          lang: 'en',
          allowCopy: true,
          allowEdit: true,
          enableAddRow: true,
          enableAddCol: true,
          cellRightClickConfig: {
            copy: true,
            copyAs: true,
            paste: true,
            insertRow: true,
            insertColumn: true,
            deleteRow: true,
            deleteColumn: true,
            deleteCell: true,
            hideRow: true,
            hideColumn: true,
            showRow: true,
            showColumn: true,
            rowHeight: true,
            columnWidth: true,
            clear: true,
            matrix: true,
            sort: true,
            filter: true,
            chart: true,
            image: true,
            link: true,
            data: true,
            cellFormat: true
          },
          hook: {
            cellUpdated: (r: number, c: number, oldValue: any, newValue: any, isRefresh: boolean) => {
              handleCellUpdate(r, c, oldValue, newValue);
            },
            cellMousedown: (r: number, c: number, value: any, event: any) => {
              handleCellSelect(r, c, value);
            },
            cellEditBefore: (r: number, c: number, value: any) => {
              return true;
            }
          }
        });
        console.log('✅ LuckySheet initialized successfully');
        return;
      } catch (error) {
        console.error('❌ LuckySheet initialization failed:', error);
      }
    }

    // Fallback: Create a simple Excel-like table
    console.log('⚠️ LuckySheet not available, using fallback Excel table');
    createFallbackExcel();
  };

  const createFallbackExcel = () => {
    if (!excelRef.current) return;

    const container = excelRef.current;
    container.innerHTML = '';

    // Create Excel-like table
    const table = document.createElement('div');
    table.className = 'fallback-excel-table';
    table.innerHTML = `
      <div class="excel-header-row">
        <div class="excel-cell header-cell">#</div>
        <div class="excel-cell header-cell">A</div>
        <div class="excel-cell header-cell">B</div>
        <div class="excel-cell header-cell">C</div>
        <div class="excel-cell header-cell">D</div>
        <div class="excel-cell header-cell">E</div>
        <div class="excel-cell header-cell">F</div>
        <div class="excel-cell header-cell">G</div>
        <div class="excel-cell header-cell">H</div>
      </div>
      ${excelData && excelData[0] && excelData[0].data ? 
        excelData[0].data.map((row: any[], rowIndex: number) => `
          <div class="excel-row">
            <div class="excel-cell row-number">${rowIndex + 1}</div>
            ${row.map((cell: any, colIndex: number) => `
              <div class="excel-cell data-cell" contenteditable="true" data-row="${rowIndex}" data-col="${colIndex}">
                ${cell.v || ''}
              </div>
            `).join('')}
          </div>
        `).join('') : 
        '<div class="no-data">No data available</div>'
      }
    `;

    container.appendChild(table);

    // Add event listeners for cell editing
    const cells = container.querySelectorAll('.data-cell');
    cells.forEach(cell => {
      cell.addEventListener('blur', (e) => {
        const target = e.target as HTMLElement;
        const row = parseInt(target.dataset.row || '0');
        const col = parseInt(target.dataset.col || '0');
        const newValue = target.textContent || '';
        handleCellUpdate(row, col, '', newValue);
      });
    });
  };

  const handleCellUpdate = async (row: number, col: number, oldValue: any, newValue: any) => {
    try {
      // Get current sheet data
      let currentSheet, sheetName;
      
      if (window.luckysheet) {
        currentSheet = window.luckysheet.getSheetData();
        sheetName = window.luckysheet.getSheetName();
      } else {
        // Fallback mode - use first sheet
        currentSheet = excelData && excelData[0] ? excelData[0].data : [];
        sheetName = excelData && excelData[0] ? excelData[0].name : 'Tasks';
      }
      
      // Determine which collection to update based on sheet name
      let collectionName = '';
      let idField = '';
      
      switch (sheetName) {
        case 'Tasks':
          collectionName = 'tasks';
          idField = 'id';
          break;
        case 'Plans':
          collectionName = 'plans';
          idField = 'id';
          break;
        case 'Blueprints':
          collectionName = 'blueprints';
          idField = 'id';
          break;
        case 'Targets':
          collectionName = 'targets';
          idField = 'id';
          break;
        default:
          return;
      }

      // Get the row data
      const rowData = currentSheet[row];
      if (!rowData || rowData.length === 0) return;

      // Get the ID from the first column
      const itemId = rowData[0]?.v;
      if (!itemId) return;

      // Map column index to field name
      const fieldMap = getFieldMap(sheetName);
      const fieldName = fieldMap[col];
      
      if (!fieldName) return;

      // Update the document
      const docRef = doc(db, collectionName, itemId);
      const updateData: any = {};
      updateData[fieldName] = newValue;
      updateData.updatedAt = new Date().toISOString();
      updateData.updatedBy = currentUser?.id;

      await updateDoc(docRef, updateData);

      // Send Excel notification for the update
      await sendExcelNotification(sheetName, itemId, fieldName, newValue, row, col);

      // Notify concerned persons
      await notifyConcernedPersons(sheetName, itemId, fieldName, newValue, row, col);

      console.log(`Updated ${sheetName} ${itemId}: ${fieldName} = ${newValue}`);
      
    } catch (error) {
      console.error('Error updating cell:', error);
    }
  };

  const getFieldMap = (sheetName: string) => {
    const fieldMaps: { [key: string]: { [key: number]: string } } = {
      'Tasks': {
        0: 'id',
        1: 'title',
        2: 'status',
        3: 'priority',
        4: 'assignedTo',
        5: 'dueDate',
        6: 'createdBy',
        7: 'updatedAt'
      },
      'Plans': {
        0: 'id',
        1: 'title',
        2: 'status',
        3: 'startDate',
        4: 'endDate',
        5: 'progress',
        6: 'owner'
      },
      'Blueprints': {
        0: 'id',
        1: 'title',
        2: 'version',
        3: 'status',
        4: 'createdBy',
        5: 'category'
      },
      'Targets': {
        0: 'id',
        1: 'title',
        2: 'status',
        3: 'targetDate',
        4: 'progress',
        5: 'assignedTo'
      }
    };
    
    return fieldMaps[sheetName] || {};
  };

  const handleCellSelect = (row: number, col: number, value: any) => {
    // Handle cell selection for user assignment
    console.log(`Selected cell: Row ${row}, Col ${col}, Value: ${value}`);
  };

  // Send Excel notification for any cell update
  const sendExcelNotification = async (sheetName: string, itemId: string, fieldName: string, newValue: any, row: number, col: number) => {
    try {
      console.log(`🔔 Sending Excel notification for ${fieldName} = ${newValue}`);
      
      // Get the item to find concerned persons
      const collectionName = sheetName.toLowerCase();
      const docRef = doc(db, collectionName, String(itemId));
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const itemData = docSnap.data();
        const concernedPersons = [];
        
        // Add assigned users
        if (itemData.assignedTo) {
          concernedPersons.push(itemData.assignedTo);
        }
        if (itemData.assignedUsers) {
          concernedPersons.push(...itemData.assignedUsers);
        }
        if (itemData.createdBy) {
          concernedPersons.push(itemData.createdBy);
        }
        
        // Send notifications to concerned persons
        for (const personId of concernedPersons) {
          if (personId !== currentUser?.id) {
            await excelNotificationService.sendExcelUpdateNotification(
              personId,
              sheetName,
              itemId,
              fieldName,
              newValue,
              currentUser?.name || 'Unknown User',
              `${String.fromCharCode(65 + col)}${row + 1}`, // Convert to Excel format (A1, B2, etc.)
              row,
              col
            );
          }
        }
      }
    } catch (error) {
      console.error('Error sending Excel notification:', error);
    }
  };

  const notifyConcernedPersons = async (sheetName: string, itemId: string, fieldName: string, newValue: any, row: number, col: number) => {
    try {
      console.log(`🔔 Notifying concerned persons for ${fieldName} = ${newValue}`);
      
      // Get the item to find concerned persons
      const collectionName = sheetName.toLowerCase();
      const docRef = doc(db, collectionName, String(itemId));
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const itemData = docSnap.data();
        const concernedPersons = [];
        
        // Add all possible concerned persons
        if (itemData.assignedTo) concernedPersons.push(itemData.assignedTo);
        if (itemData.assignedUsers) concernedPersons.push(...itemData.assignedUsers);
        if (itemData.createdBy) concernedPersons.push(itemData.createdBy);
        if (itemData.concernedPerson) concernedPersons.push(itemData.concernedPerson);
        if (itemData.responsible) concernedPersons.push(itemData.responsible);
        
        console.log(`🔔 Found concerned persons:`, concernedPersons);
        
        // Send notifications to concerned persons using the notification service
        for (const personId of concernedPersons) {
          if (personId !== currentUser?.id) {
            console.log(`🔔 Sending notification to: ${personId}`);
            await excelNotificationService.sendExcelUpdateNotification(
              personId,
              sheetName,
              itemId,
              fieldName,
              newValue,
              currentUser?.name || 'Unknown User',
              `${String.fromCharCode(65 + col)}${row + 1}`, // Convert to Excel format (A1, B2, etc.)
              row,
              col
            );
            console.log(`✅ Notification sent to: ${personId}`);
          }
        }
      } else {
        console.log(`⚠️ Document not found: ${collectionName}/${itemId}`);
      }
    } catch (error) {
      console.error('❌ Error notifying concerned persons:', error);
    }
  };


  const handleSave = async () => {
    try {
      console.log('💾 Saving complete Excel workbook...');
      
      if (!currentUser) {
        console.error('❌ No current user found');
        return;
      }
      
      // Get current LuckySheet data
      const currentData: LuckySheetData[] = window.luckysheet.getAllSheets();
      console.log('📊 Current Excel data:', currentData);
      
      // Convert LuckySheet data to our format
      const sheets = currentData.map((sheet: LuckySheetData) => ({
        name: sheet.name,
        data: sheet.data || [],
        selectedCell: null,
        selectedRange: null
      }));
      
      // Create workbook object
      const workbook: ExcelWorkbook = {
        id: `workbook_${folderId}_${Date.now()}`,
        folderId: folderId,
        workbookName: `${folderName} - Excel Workbook`,
        sheets: sheets,
        columnWidths: {},
        rowHeights: {},
        version: 1,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: currentUser.id,
        updatedBy: currentUser.id
      };
      
      // Save to AWS S3 or Firestore fallback
      const result = await excelAwsS3Service.saveWorkbook(workbook);
      
      if (result.success) {
        console.log('✅ Complete Excel workbook saved successfully');
        alert('Excel workbook saved successfully!');
      } else {
        throw new Error(result.error || 'Failed to save workbook');
      }
      
    } catch (error) {
      console.error('❌ Error saving Excel workbook:', error);
      alert('Error saving Excel workbook. Please try again.');
    }
  };

  const handleRefresh = async () => {
    try {
      console.log('🔄 Refreshing Excel data...');
      await loadFolderData();
      console.log('✅ Excel data refreshed successfully');
    } catch (error) {
      console.error('❌ Error refreshing Excel data:', error);
    }
  };

  const handleExport = () => {
    // Export to Excel file
    window.luckysheet.exportExcel();
  };

  // Handle multiple user selection
  const handleUserSelection = async (userNames: string) => {
    setSelectedUsers(userNames);
    console.log('👥 Multiple users selected:', userNames);
    
    // If we have LuckySheet available, we can update a cell with the selected users
    if (window.luckysheet && userNames) {
      // Get current sheet data
      const currentSheet = window.luckysheet.getSheetData();
      const sheetName = window.luckysheet.getSheetName();
      
      // Find a suitable cell to update (e.g., first empty cell in column A)
      let targetRow = 1; // Start from row 1 (0-indexed)
      let targetCol = 0; // Column A
      
      // Find first empty cell in column A
      while (targetRow < currentSheet.length && currentSheet[targetRow] && currentSheet[targetRow][targetCol]) {
        targetRow++;
      }
      
      // Update the cell with selected users
      window.luckysheet.setCellValue(targetRow, targetCol, userNames);
      console.log(`📝 Updated cell [${targetRow}, ${targetCol}] with users: ${userNames}`);
      
      // Send notifications to all selected users
      try {
        const userNamesList = userNames.split(',').map(name => name.trim()).filter(name => name);
        console.log(`🔔 Sending notifications to ${userNamesList.length} users:`, userNamesList);
        
        // Send notifications to each user
        for (const userName of userNamesList) {
          try {
            // Send in-app notification
            // OLD SERVICE - DISABLED: 
            // await excelAwsNotificationService.sendExcelAssignmentNotification(
            //   'excel-container-user', // We'll need to get actual user ID
            //   sheetName,
            //   `Assigned: ${userName}`,
            //   currentUser?.id || 'unknown',
            //   `A${targetRow + 1}`
            // );
            console.log(`✅ Notification sent to ${userName}`);
          } catch (error) {
            console.error(`❌ Failed to send notification to ${userName}:`, error);
          }
        }
        
        console.log(`📊 Notifications sent to ${userNamesList.length} users`);
      } catch (error) {
        console.error('❌ Error sending notifications to multiple users:', error);
      }
    }
  };

  // Test notification function for debugging
  const testNotification = async () => {
    try {
      console.log('🧪 Testing notification system...');
      if (!currentUser) {
        alert('No user logged in');
        return;
      }
      
      // Create a test notification
      await excelNotificationService.createTestNotification(currentUser.id);
      console.log('✅ Test notification created');
      alert('Test notification sent! Check the notification bell.');
    } catch (error) {
      console.error('❌ Error creating test notification:', error);
      alert('Error creating test notification: ' + error);
    }
  };

  if (isLoading) {
    return (
      <div className="excel-loading">
        <div className="loading-spinner"></div>
        <p>Loading Excel data...</p>
      </div>
    );
  }

  return (
    <div className="excel-container">
      <div className="excel-header">
        <div className="excel-title">
          <h2>📊 {folderName} - Excel View</h2>
          <p>Real-time collaborative editing</p>
        </div>
        <div className="excel-actions">
          <button onClick={handleRefresh} className="btn-refresh">
            🔄 Refresh
          </button>
          <button onClick={handleSave} className="btn-save">
            💾 Save
          </button>
          <button onClick={handleExport} className="btn-export">
            📤 Export
          </button>
          <button onClick={testNotification} className="btn-test" style={{background: '#ff6b6b', color: 'white', fontWeight: 'bold'}}>
            🔔 Test Notification
          </button>
          <button 
            onClick={() => setShowUserSelector(!showUserSelector)} 
            className="btn-users" 
            style={{background: '#10b981', color: 'white', fontWeight: 'bold'}}
          >
            👥 Select Multiple Users
          </button>
          <button onClick={onClose} className="btn-close">
            ❌ Close
          </button>
        </div>
      </div>
      
      <div className="excel-toolbar">
        <div className="toolbar-section">
          <span className="toolbar-label">Sheets:</span>
          <div className="sheet-tabs">
            <div className="sheet-tab active">Tasks</div>
            <div className="sheet-tab">Plans</div>
            <div className="sheet-tab">Blueprints</div>
            <div className="sheet-tab">Targets</div>
          </div>
        </div>
        <div className="toolbar-section">
          <span className="toolbar-label">Users Online:</span>
          <div className="online-users">
            {users.map(user => (
              <div key={user.id} className="user-indicator" style={{ backgroundColor: user.color }}>
                {user.name.charAt(0).toUpperCase()}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="excel-content">
        <div ref={excelRef} id="luckysheet" className="excel-sheet"></div>
      </div>

      {/* Multiple User Selection Modal */}
      {showUserSelector && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              borderBottom: '1px solid #e5e7eb',
              paddingBottom: '10px'
            }}>
              <h3 style={{ margin: 0, color: '#1f2937', fontSize: '18px' }}>
                👥 Select Multiple Users
              </h3>
              <button
                onClick={() => setShowUserSelector(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ✕
              </button>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                Select Users:
              </label>
              <UserSelector
                value={selectedUsers}
                onChange={handleUserSelection}
                onBlur={() => {}}
                placeholder="Select multiple users..."
                multiple={true}
                style={{
                  width: '100%',
                  minHeight: '40px'
                }}
              />
            </div>
            
            {selectedUsers && (
              <div style={{
                background: '#f3f4f6',
                padding: '10px',
                borderRadius: '6px',
                marginBottom: '20px'
              }}>
                <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '5px' }}>
                  Selected Users:
                </div>
                <div style={{ fontSize: '16px', color: '#1f2937', fontWeight: '500' }}>
                  {selectedUsers}
                </div>
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowUserSelector(false)}
                style={{
                  padding: '8px 16px',
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (selectedUsers) {
                    handleUserSelection(selectedUsers);
                    setShowUserSelector(false);
                  }
                }}
                disabled={!selectedUsers}
                style={{
                  padding: '8px 16px',
                  background: selectedUsers ? '#3b82f6' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: selectedUsers ? 'pointer' : 'not-allowed'
                }}
              >
                Add to Excel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExcelContainer;
